import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  resetSiteChartFilter,
  selectSiteChartFilter,
  setSiteChartFilter,
} from "../../../../../redux/features/site-chart-filter/site-chart-filter-slice";
import { ESolarPlantViewType } from "../../../../../shared/oversight-core/enums/solar-plant-view-type";
import FilteringSidePanel from "../../../../../shared/oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppDatePicker from "../../../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import { Option } from "../../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import { getSolarPlantViewType } from "../../../../../shared/oversight-core/utils/get-solar-plant-view-type";
import { mergeDateAndTime } from "../../../../../shared/oversight-core/utils/time-utils";
import styles from "./vrm-site-dashboard-filters.module.scss";

interface IProps {
  show: boolean;
  onClose: () => void;
  setQuickFilter: React.Dispatch<React.SetStateAction<Option>>;
  quickFilter: Option;
}

const getMidnight = () => {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  return midnight;
};

const toDateTime = (date: Date, time: Date) =>
  mergeDateAndTime(date, moment(time).format("HH:mm"));

const quickFilterOptionsOne: Option[] = [
  { value: ESolarPlantViewType.LAST_HOUR, label: "Last hour" },
  { value: ESolarPlantViewType.LAST_THREE_HOURS, label: "Last 3 hours" },
  { value: ESolarPlantViewType.LAST_SIX_HOURS, label: "Last 6 hours" },
  { value: ESolarPlantViewType.LAST_TWELVE_HOURS, label: "Last 12 hours" },
  {
    value: ESolarPlantViewType.LAST_TWENTY_FOUR_HOURS,
    label: "Last 24 hours",
  },
];

const quickFilterOptionsTwo: Option[] = [
  { value: ESolarPlantViewType.TODAY, label: "Today" },
  { value: ESolarPlantViewType.THIS_WEEK, label: "This week" },
  { value: ESolarPlantViewType.THIS_MONTH, label: "This month" },
  { value: ESolarPlantViewType.THIS_YEAR, label: "This year" },
  { value: ESolarPlantViewType.LAST_YEAR, label: "Last year" },
];

const quickFilterOptionsThree: Option[] = [
  { value: ESolarPlantViewType.YESTERDAY, label: "Yesterday" },
  { value: ESolarPlantViewType.DAY_BEFORE, label: "Day before" },
  {
    value: ESolarPlantViewType.THIS_DAY_LAST_WEEK,
    label: "This day last week",
  },
  { value: ESolarPlantViewType.PREVIOUS_MONTH, label: "Previous month" },
  { value: ESolarPlantViewType.PREVIOUS_WEEK, label: "Previous week" },
];

const quickFilterOptionsFour: Option[] = [
  { value: ESolarPlantViewType.LAST_TWO_DAYS, label: "Last 2 days" },
  { value: ESolarPlantViewType.LAST_SEVEN_DAYS, label: "Last 7 days" },
  { value: ESolarPlantViewType.LAST_THIRTY_DAYS, label: "Last 30 days" },
  { value: ESolarPlantViewType.LAST_NINETY_DAYS, label: "Last 90 days" },
  { value: ESolarPlantViewType.LAST_SIX_MONTHS, label: "Last 6 months" },
  {
    value: ESolarPlantViewType.LAST_TWELVE_MONTHS,
    label: "Last 12 months",
  },
];

const VrmSiteDashboardFilters = (props: IProps) => {
  const { show, onClose, setQuickFilter, quickFilter } = props;

  const appliedFilter = useSelector(selectSiteChartFilter);

  const [startDate, setStartDate] = useState(getMidnight);
  const [endDate, setEndDate] = useState(() => new Date());
  const [startTime, setStartTime] = useState(getMidnight);
  const [endTime, setEndTime] = useState(() => new Date());

  const dispatch = useDispatch();

  const setFormRange = (start: Date, end: Date) => {
    setStartDate(start);
    setStartTime(start);
    setEndDate(end);
    setEndTime(end);
  };

  // The form shows the applied custom range each time the drawer opens (or today so far when a
  // quick filter is applied), so a range that was never applied does not come back.
  useEffect(() => {
    if (!show) {
      return;
    }

    if (appliedFilter.isFilterCustom) {
      setFormRange(
        new Date(appliedFilter.startDate),
        new Date(appliedFilter.endDate),
      );
    } else {
      setFormRange(getMidnight(), new Date());
    }
  }, [show, appliedFilter]);

  const fromDateTime = toDateTime(startDate, startTime);
  const toDateTimeValue = toDateTime(endDate, endTime);
  // A range has to cover some time: From must be strictly before To. A reversed range and one where
  // both ends are the same time each get their own message.
  const rangeError =
    fromDateTime > toDateTimeValue
      ? "From date and time cannot be later than To date and time"
      : fromDateTime.getTime() === toDateTimeValue.getTime()
        ? "From and To date and time cannot be the same"
        : undefined;
  const isRangeValid = !rangeError;

  return (
    <FilteringSidePanel isOpen={show} onClose={onClose} isHideButtons>
      <>
        <Row>
          <Col className="text-light font-weight-400 font-size-12">From</Col>
        </Row>
        <Row>
          <Col className="pe-0">
            <AppDatePicker
              selectedDate={startDate}
              onChange={(date) => {
                setStartDate(date);
              }}
              isInput
              inputDateFormat="yyyy-MM-dd"
              fontSize={12}
            />
          </Col>
          <Col>
            <AppDatePicker
              selectedDate={startTime}
              onChange={(time) => {
                setStartTime(time);
              }}
              isInput
              isInputTime
              inputDateFormat="HH:mm"
              showTimeSelect
              showTimeSelectOnly
              fontSize={12}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-light font-weight-400 font-size-12">To</Col>
        </Row>
        <Row>
          <Col className="pe-0">
            <AppDatePicker
              selectedDate={endDate}
              onChange={(date) => {
                setEndDate(date);
              }}
              isInput
              inputDateFormat="yyyy-MM-dd"
              fontSize={12}
            />
          </Col>
          <Col>
            <AppDatePicker
              selectedDate={endTime}
              onChange={(time) => {
                setEndTime(time);
              }}
              isInput
              isInputTime
              inputDateFormat="HH:mm"
              showTimeSelect
              showTimeSelectOnly
              fontSize={12}
            />
          </Col>
        </Row>
        {rangeError && <div className="error mt-2">{rangeError}</div>}
        <Row className="align-items-center mt-4 pt-2">
          <Col className="col-auto">
            <AppButton
              text="Filter"
              variant="blue"
              size="medium"
              className="px-5"
              disabled={!isRangeValid}
              onClick={() => {
                if (!isRangeValid) {
                  return;
                }

                dispatch(
                  setSiteChartFilter({
                    startDate: fromDateTime.toISOString(),
                    endDate: toDateTimeValue.toISOString(),
                    viewType: getSolarPlantViewType(
                      fromDateTime,
                      toDateTimeValue,
                    ),
                    isFilterCustom: true,
                  }),
                );
                onClose();
              }}
            />
          </Col>
          <Col className="col-auto ps-0">
            <AppButton
              text="Clear All"
              variant="transparent"
              onClick={() => {
                // Back to today, applied at once, and the drawer closes.
                dispatch(resetSiteChartFilter());
                setFormRange(getMidnight(), new Date());
                setQuickFilter({
                  value: ESolarPlantViewType.TODAY,
                  label: "Today",
                });
                onClose();
              }}
              size="medium"
              className="px-3"
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-dark font-weight-500 font-size-14 text-center">
            Quick Filters
          </Col>
        </Row>
        <Row className="mt-2 mx-2">
          <Col>
            {quickFilterOptionsOne.map((i, index) => {
              return (
                <Row key={index} className="mt-3">
                  <Col
                    className={`col-auto font-weight-500 font-size-12 cursor-pointer ${
                      quickFilter.value === i.value
                        ? `text-primary`
                        : `text-light`
                    }`}
                    onClick={() => {
                      setQuickFilter(i);
                      dispatch(resetSiteChartFilter());
                      onClose();
                    }}
                  >
                    {i.label}
                  </Col>
                </Row>
              );
            })}
          </Col>
          <Col className={`${styles.verticalLine} ps-4 pe-0`}>
            {quickFilterOptionsTwo.map((i, index) => {
              return (
                <Row key={index} className="mt-3">
                  <Col
                    className={`col-auto font-weight-500 font-size-12 cursor-pointer ${
                      quickFilter.value === i.value
                        ? `text-primary`
                        : `text-light`
                    }`}
                    onClick={() => {
                      setQuickFilter(i);
                      dispatch(resetSiteChartFilter());
                      onClose();
                    }}
                  >
                    {i.label}
                  </Col>
                </Row>
              );
            })}
          </Col>
        </Row>
        <div className={`${styles.horizontalLine} mt-4 mb-2`}></div>
        <Row className="mx-2">
          <Col>
            {quickFilterOptionsThree.map((i, index) => {
              return (
                <Row key={index} className="mt-3">
                  <Col
                    className={`col-auto font-weight-500 font-size-12 cursor-pointer ${
                      quickFilter.value === i.value
                        ? `text-primary`
                        : `text-light`
                    }`}
                    onClick={() => {
                      setQuickFilter(i);
                      dispatch(resetSiteChartFilter());
                      onClose();
                    }}
                  >
                    {i.label}
                  </Col>
                </Row>
              );
            })}
          </Col>
          <Col className={`${styles.verticalLine} ps-4 pe-0`}>
            {quickFilterOptionsFour.map((i, index) => {
              return (
                <Row key={index} className="mt-3">
                  <Col
                    className={`col-auto font-weight-500 font-size-12 cursor-pointer ${
                      quickFilter.value === i.value
                        ? `text-primary`
                        : `text-light`
                    }`}
                    onClick={() => {
                      setQuickFilter(i);
                      dispatch(resetSiteChartFilter());
                      onClose();
                    }}
                  >
                    {i.label}
                  </Col>
                </Row>
              );
            })}
          </Col>
        </Row>
      </>
    </FilteringSidePanel>
  );
};

export default VrmSiteDashboardFilters;
